import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { HexLogo } from "@/components/dashboard/Hex";
import { BrutalButton } from "@/components/dashboard/BentoCard";
import { InstallPrompt } from "@/components/marketing/InstallPrompt";

export function MarketingShell({ children, hideStrip = false }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-honeycomb text-black flex flex-col">
      {!hideStrip ? (
        <div className="bg-red-600 text-white text-center py-2 font-mono-data text-[11px] uppercase tracking-widest">
          AI scores every listing · alerts before the auction · 3-day free trial
        </div>
      ) : null}

      <header className="border-b-2 border-black bg-brand-black">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-5">
          <Link to="/" className="flex items-center gap-3 group">
            <HexLogo size={44} />
            <div>
              <div className="font-brand text-xl leading-none text-red-500 group-hover:text-red-400">
                LIEN SCOUT
              </div>
              <div className="font-mono-data text-[9px] uppercase tracking-[0.18em] text-yellow-400 mt-1.5">
                AI Powered Property Intelligence
              </div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm font-display font-semibold text-white/80">
            <Link to="/features" className="hover:text-yellow-400" data-testid="marketing-nav-features">Features</Link>
            <Link to="/pricing" className="hover:text-yellow-400" data-testid="marketing-nav-pricing">Pricing</Link>
            <Link to="/coverage" className="hover:text-yellow-400" data-testid="marketing-nav-coverage">Coverage</Link>
            <Link to="/blog" className="hover:text-yellow-400" data-testid="marketing-nav-blog">Blog</Link>
            <Link to="/about" className="hover:text-yellow-400" data-testid="marketing-nav-about">About</Link>
            <Link to="/contact" className="hover:text-yellow-400" data-testid="marketing-nav-contact">Contact</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="hidden sm:inline font-display text-sm font-semibold text-white/90 hover:text-yellow-400"
              data-testid="marketing-nav-signin"
            >
              Sign in
            </Link>
            <BrutalButton
              data-testid="marketing-nav-cta"
              onClick={() => navigate("/login")}
            >
              Start free trial <ArrowRight size={14} />
            </BrutalButton>
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <MarketingFooter />
      <InstallPrompt />
    </div>
  );
}

export function MarketingFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-brand-black text-white/80 border-t-2 border-black">
      <div className="max-w-6xl mx-auto px-6 py-12 grid md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <HexLogo size={40} />
            <div>
              <div className="font-brand text-lg text-red-500">LIEN SCOUT</div>
              <div className="font-mono-data text-[9px] uppercase tracking-[0.18em] text-yellow-400 mt-1">
                AI Powered Property Intelligence
              </div>
            </div>
          </div>
          <p className="mt-4 text-sm text-white/70 max-w-md">
            Nationwide public tax-lien and tax-sale intelligence, ranked by AI
            Deal Score. The list is free. The shortlist is what you pay for.
          </p>
          <p className="mt-4 font-mono-data text-[10px] uppercase tracking-widest text-white/50">
            © {year} Lien Scout. Public-record data · not legal or investment advice.
          </p>
        </div>
        <div>
          <div className="font-display font-bold text-xs uppercase tracking-widest text-yellow-400 mb-3">Product</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/features" className="hover:text-yellow-400">Features</Link></li>
            <li><Link to="/coverage" className="hover:text-yellow-400">Coverage map</Link></li>
            <li><Link to="/pricing" className="hover:text-yellow-400">Pricing</Link></li>
            <li><Link to="/blog" className="hover:text-yellow-400">Blog</Link></li>
            <li><Link to="/alerts" className="hover:text-yellow-400">Alerts</Link></li>
          </ul>
        </div>
        <div>
          <div className="font-display font-bold text-xs uppercase tracking-widest text-yellow-400 mb-3">Company</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-yellow-400">About</Link></li>
            <li><Link to="/contact" className="hover:text-yellow-400">Contact</Link></li>
            <li><Link to="/legal/terms" className="hover:text-yellow-400">Terms of Service</Link></li>
            <li><Link to="/legal/privacy" className="hover:text-yellow-400">Privacy Policy</Link></li>
          </ul>
          <div className="mt-4 text-xs text-white/50 font-mono-data">
            <a href="mailto:sbrown@lienscoutapp.com" className="hover:text-yellow-400">sbrown@lienscoutapp.com</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
